import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  ModuleWithProviders,
  NgModule,
  Optional,
  SkipSelf,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { Action, ActionReducerMap, MetaReducer, StoreModule } from '@ngrx/store';
import { appConfigProvider } from 'app/core/app-config/app-config.interface';
import { AuthService } from 'app/core/authentication/services/auth.service';
import { BrowserService } from 'app/core/browser/services/browser.service';
import { LanguageRestConnectorService } from 'app/core/language/services/language-rest-connector.service';
import { LanguageService } from 'app/core/language/services/language.service';
import { LanguageEffects, languageReducer } from 'app/core/language/state';
import { MaterialModule } from 'app/material.module';
import { environment } from 'environments/environment';
import { storeFreeze } from 'ngrx-store-freeze';

import { AppConfigService } from './app-config/services/app-config.service';
import { AppStateInterface } from './app-config/state/models';
import { appStartupFactory } from './app-startup-factory';
import { AuthenticationGuard } from './authentication/authentication.guard';
import { AuthActionType } from './authentication/state/actions';
import { AuthEffects } from './authentication/state/auth-effects';
import { authReducer } from './authentication/state/auth.reducer';
import { LoggingServiceInterface, loggingServiceProvider, WINDOW_PROVIDERS } from './browser/services';
import { RouterRequestEffects } from './browser/state/router-request-effects';
import { ErrorHandlerService } from './error-handler/services/error-handler.service';
import { ErrorEffects } from './error-handler/state/error-effects';
import { errorReducer } from './error-handler/state/error.reducer';
import { LoggingService } from './logging/services/logging.service';
import { traceLogReducer } from './trace-log/state/trace-log.reducer';
import { UserRestConnectorService, UserService } from './user/services';
import { UserEffects } from './user/state/user-effects';
import { userReducer } from './user/state/user.reducer';

const reducers: ActionReducerMap<Partial<AppStateInterface>> = {
  error: errorReducer,
  user: userReducer,
  auth: authReducer,
  language: languageReducer,
};

type LogoutMetaReducer = (state: AppStateInterface, action: Action) => {};

// reference: https://stackoverflow.com/a/45324038
export function logout(reducer: Function): LogoutMetaReducer {
  return (state: AppStateInterface, action: Action): AppStateInterface => {
    return reducer(action.type === AuthActionType.Logout ? undefined : state, action);
  };
}

const metaReducers: MetaReducer<any>[] = environment.production ?
  [logout] : [traceLogReducer, storeFreeze, logout];

export interface CoreProvidersInterface {
  loggingService?: new (...args: any[]) => LoggingServiceInterface;
}

@NgModule({
  declarations: [],
  imports: [
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot([
      ErrorEffects,
      RouterRequestEffects,
      UserEffects,
      AuthEffects,
      LanguageEffects,
    ]),
    CommonModule,
    HttpClientModule,
    RouterModule,
    MaterialModule,
  ],
  exports: [
  ],
  providers: [
    appConfigProvider(environment), // TODO: make this customizable
    AppConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: appStartupFactory,
      deps: [AuthService],
      multi: true,
    },
    ErrorHandlerService,
    BrowserService,
    AuthService,
    UserService,
    UserRestConnectorService,
    AuthenticationGuard,
    LanguageService,
    LanguageRestConnectorService,
  ],
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA,
  ],
})
export class CoreModule {
  // https://angular.io/guide/singleton-services#prevent-reimport-of-the-coremodule
  constructor(
    @Optional()
    @SkipSelf()
    parentModule: CoreModule) {
    if (parentModule) {
      throw new Error('CoreModule is already loaded');
    }
  }

  public static forRoot(providers?: CoreProvidersInterface): ModuleWithProviders {
    return {
      ngModule: CoreModule,
      providers: [
        loggingServiceProvider(providers ? (providers.loggingService || LoggingService) : LoggingService),
        WINDOW_PROVIDERS,
      ],
    };
  }
}
